import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import Layout from "./components/Layout";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { UPDATE_ADMIN_PASS_VALIDATION } from "../../validations/admins";
import { useMutation, useQuery } from "@tanstack/react-query";
import walletService from "../../service/walletService";
import { LockOutlined as LockOutlinedIcon } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import userservice from "../../service/userservice";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { Button, TextField } from "@mui/material";
import BTNLoader from "../../lib/components/BTNLoader";

const defaultValues = {
  password: "",
  newPassword: "",
  confirmNewPassword: "",
};
const UserPassword = () => {
  const [pinModalOpen, setPinModalOpen] = React.useState(false);
  const { data: pinStatus, isLoading: isLoadingPinStatus, refetch: refetchPinStatus } = useQuery({
    queryKey: ["adminPinStatus"],
    queryFn: () => walletService.getPinStatus().then((res) => res?.data?.data),
    retry: false,
  });

  const formHook = useForm({
    resolver: yupResolver(UPDATE_ADMIN_PASS_VALIDATION),
    mode: "all",
    defaultValues: defaultValues,
  });

  const errors = formHook.formState.errors;
  const control = formHook.control;
  const handleSubmit = formHook.handleSubmit;
  const reset = formHook.reset;

  const mutation = useMutation({
    mutationFn: (data) => userservice.changePassword(data),
    onError: ({ response }) => errorHandler(response),
    onSuccess: ({ data }) => {
      onCreateSuccess(data);
    },
  });

  async function save(data) {
    mutation.mutate(data);
  }
  const { isPending } = mutation;
  return (
    <AppLayout>
      <Header
        title="Settings"
        description="Customize your account preferences and general settings here."
      />
      <Layout>
        <div className="p-4">
          <div className="fw-medium fs-4 mb-3">Change Password</div>
          <form onSubmit={handleSubmit(save)} style={{ maxWidth: 450 }}>
            <div className="mb-4">
              <label className="d-block">
                Current Password
                <span className="text-danger">*</span>
              </label>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    type="password"
                    variant="outlined"
                    placeholder="************"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors["password"]?.message}
                    error={!!errors["password"]}
                  />
                )}
              />
            </div>
            <div className="mb-4">
              <label className="d-block">
                New Password
                <span className="text-danger">*</span>
              </label>
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    type="password"
                    placeholder="************"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors["newPassword"]?.message}
                    error={!!errors["newPassword"]}
                  />
                )}
              />
            </div>
            <div className="mb-4">
              <label className="d-block">
                Confirm New Password
                <span className="text-danger">*</span>
              </label>
              <Controller
                name="confirmNewPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    type="password"
                    placeholder="************"
                    value={field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                    helperText={errors["confirmNewPassword"]?.message}
                    error={!!errors["confirmNewPassword"]}
                  />
                )}
              />
            </div>

            <div className="mb-4 d-flex justify-content-end">
              <Button
                type="submit"
                disabled={isPending}
                startIcon={<BTNLoader isLoading={isPending} />}
                variant="contained"
                color={"secondary"}
              >
                Save
              </Button>
            </div>
          </form>

          <hr className="my-5" />
          <div className="fw-medium fs-4 mb-3">Payment PIN</div>
          <div className="card mb-4 border border-secondary-subtle">
            <div className="card-body d-flex align-items-center justify-content-between p-4">
              <div className="d-flex align-items-center gap-3">
                <div className="bg-light p-3 rounded-3 border border-secondary-subtle">
                  <LockOutlinedIcon className="text-secondary fs-3" />
                </div>
                <div>
                  <h5 className="mb-1 fw-bold fs-6">
                    {pinStatus?.isSet ? "Change Payment PIN" : "Set Up Payment PIN"}
                  </h5>
                  <p className="mb-0 text-muted fs-sm">
                    Set up a 4-digit PIN to verify withdrawals and keep your account secure.
                  </p>
                </div>
              </div>
              <div>
                <Button
                  variant="outlined"
                  color="secondary"
                  className="bg-white px-4"
                  onClick={() => setPinModalOpen(true)}
                  disabled={isLoadingPinStatus}
                >
                  {pinStatus?.isSet ? "Change PIN" : "Set PIN"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Layout>
      <AdminPinSettingsModal
        open={pinModalOpen}
        onClose={() => setPinModalOpen(false)}
        isPinSet={Boolean(pinStatus?.isSet)}
        refetch={refetchPinStatus}
      />
    </AppLayout>
  );

  function onCreateSuccess(data) {
    toast.success(`Password updated successfully`);
    reset(defaultValues);
  }
};

const PinInput = ({ value, onChange, length = 4, disabled, prefix = "pin" }) => {
  const handleInputChange = (e, idx) => {
    const val = e.target.value.replace(/[^0-9]/g, "");
    if (!val) return;
    
    const newVal = value.split("");
    newVal[idx] = val[val.length - 1];
    const result = newVal.join("").slice(0, length);
    onChange(result);

    if (idx < length - 1) {
      const nextInput = document.getElementById(`${prefix}-box-${idx + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (e, idx) => {
    if (e.key === "Backspace") {
      const newVal = value.split("");
      newVal[idx] = "";
      const result = newVal.join("");
      onChange(result);

      if (idx > 0) {
        const prevInput = document.getElementById(`${prefix}-box-${idx - 1}`);
        prevInput?.focus();
      }
    }
  };

  return (
    <div className="d-flex gap-2 justify-content-center my-3">
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          id={`${prefix}-box-${idx}`}
          type="password"
          maxLength={1}
          disabled={disabled}
          value={value[idx] || ""}
          onChange={(e) => handleInputChange(e, idx)}
          onKeyDown={(e) => handleKeyDown(e, idx)}
          className="form-control text-center fw-bold fs-4 bg-light focus-border-primary"
          style={{ width: "50px", height: "50px" }}
        />
      ))}
    </div>
  );
};

const AdminPinSettingsModal = ({ open, onClose, isPinSet, refetch }) => {
  const [mode, setMode] = React.useState("FORM"); // FORM, RESET_REQUEST, RESET_VERIFY
  const [currentPin, setCurrentPin] = React.useState("");
  const [newPin, setNewPin] = React.useState("");
  const [confirmPin, setConfirmPin] = React.useState("");
  const [otp, setOtp] = React.useState("");
  const [errorText, setErrorText] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMode("FORM");
      setCurrentPin("");
      setNewPin("");
      setConfirmPin("");
      setOtp("");
      setErrorText("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorText("");

    if (isPinSet) {
      if (currentPin.length !== 4) {
        setErrorText("Please enter your current 4-digit PIN");
        return;
      }
      if (newPin.length !== 4) {
        setErrorText("Please enter your new 4-digit PIN");
        return;
      }
      if (newPin !== confirmPin) {
        setErrorText("New PIN and confirmation do not match");
        return;
      }
      setIsSubmitting(true);
      try {
        await walletService.changePin({ currentPin, newPin });
        toast.success("Payment PIN updated successfully!");
        refetch();
        onClose();
      } catch (err) {
        const errMsg = err?.response?.data?.message || err?.message || "Failed to update PIN";
        setErrorText(errMsg);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (newPin.length !== 4) {
        setErrorText("Please enter a 4-digit PIN");
        return;
      }
      if (newPin !== confirmPin) {
        setErrorText("PINs do not match");
        return;
      }
      setIsSubmitting(true);
      try {
        await walletService.setPin({ pin: newPin });
        toast.success("Payment PIN set successfully!");
        refetch();
        onClose();
      } catch (err) {
        const errMsg = err?.response?.data?.message || err?.message || "Failed to set PIN";
        setErrorText(errMsg);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleResetRequest = async () => {
    setIsSubmitting(true);
    setErrorText("");
    try {
      const res = await walletService.requestPinReset();
      setMode("RESET_VERIFY");
      toast.success(res?.data?.data?.message || "OTP reset code sent to email");
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Failed to send OTP code";
      setErrorText(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 5) {
      setErrorText("Please enter the 5-digit verification code");
      return;
    }
    if (newPin.length !== 4) {
      setErrorText("Please enter your new 4-digit PIN");
      return;
    }
    setIsSubmitting(true);
    setErrorText("");
    try {
      await walletService.resetPin({ otp, newPin });
      toast.success("PIN reset successfully!");
      refetch();
      onClose();
    } catch (err) {
      const errMsg = err?.response?.data?.message || err?.message || "Reset failed";
      setErrorText(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="text-center fw-bold fs-5">
        {mode === "FORM"
          ? (isPinSet ? "Change Payment PIN" : "Set Up Payment PIN")
          : "Reset Payment PIN"}
      </DialogTitle>
      <DialogContent>
        {mode === "FORM" && (
          <form onSubmit={handleSubmit}>
            {isPinSet && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label fs-xs text-uppercase text-secondary mb-0">Current PIN</label>
                  <Button
                    size="small"
                    variant="text"
                    color="secondary"
                    className="p-0 text-lowercase"
                    onClick={handleResetRequest}
                    disabled={isSubmitting}
                  >
                    Forgot PIN?
                  </Button>
                </div>
                <PinInput value={currentPin} onChange={setCurrentPin} prefix="current" disabled={isSubmitting} />
              </div>
            )}
            <div className="mb-3">
              <label className="form-label fs-xs text-uppercase text-secondary text-center d-block">
                {isPinSet ? "New PIN" : "Enter PIN"}
              </label>
              <PinInput value={newPin} onChange={setNewPin} prefix="new" disabled={isSubmitting} />
            </div>
            <div className="mb-3">
              <label className="form-label fs-xs text-uppercase text-secondary text-center d-block">
                Confirm PIN
              </label>
              <PinInput value={confirmPin} onChange={setConfirmPin} prefix="confirm" disabled={isSubmitting} />
            </div>

            {errorText && (
              <div className="text-danger text-center fs-xs mb-3">{errorText}</div>
            )}

            <DialogActions className="px-0">
              <Button onClick={onClose} disabled={isSubmitting} variant="outlined" color="secondary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || newPin.length !== 4 || confirmPin.length !== 4}
                variant="contained"
                color="secondary"
              >
                Save
              </Button>
            </DialogActions>
          </form>
        )}

        {mode === "RESET_VERIFY" && (
          <form onSubmit={handleResetSubmit}>
            <p className="text-muted text-center fs-sm mb-4">
              Enter the 5-digit code sent to your email and your new PIN.
            </p>
            <div className="mb-3">
              <TextField
                fullWidth
                size="small"
                label="Verification Code (OTP)"
                placeholder="12345"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
                disabled={isSubmitting}
              />
            </div>
            <div className="mb-3">
              <label className="form-label fs-xs text-uppercase text-secondary text-center d-block">New PIN</label>
              <PinInput value={newPin} onChange={setNewPin} prefix="new-reset" disabled={isSubmitting} />
            </div>

            {errorText && (
              <div className="text-danger text-center fs-xs mb-3">{errorText}</div>
            )}

            <DialogActions className="px-0">
              <Button onClick={() => setMode("FORM")} disabled={isSubmitting} variant="outlined" color="secondary">
                Back
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || otp.length !== 5 || newPin.length !== 4}
                variant="contained"
                color="secondary"
              >
                Reset PIN
              </Button>
            </DialogActions>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserPassword;
